/**
 * بوب أب يظهر عند محاولة التبرع (أرض أو عقد صيانة) دون أن يكون المستخدم متبرعاً.
 * يعرض رسالة توضيحية حسب حالة تسجيل الدخول.
 * يضيف returnTo/returnUrl للعودة للصفحة بعد إكمال التسجيل.
 */
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

type NotBenefactorModalProps = {
  open: boolean;
  onClose: () => void;
  /** إذا true = المستخدم مسجّل دخول وليس متبرعاً. إذا false = غير مسجّل */
  isLoggedIn?: boolean;
};

const MESSAGE_LOGGED_IN =
  "للاستفادة من خدمة الأراضي الشاغرة لبناء مسجد أو خدمات عقود الصيانة، يجب تسجيل حسابك كـ متبرع أولاً.";
const MESSAGE_GUEST =
  "للاستفادة من خدمات المساجد الإلكترونية، يرجى تسجيل الدخول أو إنشاء حساب جديد.";

const SIGNUP_LABEL = "إنشاء حساب متبرع";
const LOGIN_LABEL = "تسجيل الدخول";
const CREATE_ACCOUNT_LABEL = "إنشاء حساب";
const SIGNUP_PATH = "/benefaction/signup";
const LOGIN_PATH = "/benefaction/login";
const RESIDENT_SIGNUP_PATH = "/resident-signup";

function safeReturnPath(path: string): string | null {
  const p = (path || "").trim();
  if (!p || !p.startsWith("/") || p.startsWith("//")) return null;
  return p;
}

export default function NotBenefactorModal({ open, onClose, isLoggedIn = true }: NotBenefactorModalProps) {
  const location = useLocation();

  const primaryPath = useMemo(() => {
    const returnPath = safeReturnPath(location.pathname + location.search);
    if (isLoggedIn) {
      return returnPath ? `${SIGNUP_PATH}?returnTo=${encodeURIComponent(returnPath)}` : SIGNUP_PATH;
    }
    return returnPath ? `${LOGIN_PATH}?returnUrl=${encodeURIComponent(returnPath)}` : LOGIN_PATH;
  }, [isLoggedIn, location.pathname, location.search]);

  const createAccountPath = useMemo(() => {
    const returnPath = safeReturnPath(location.pathname + location.search);
    return returnPath ? `${RESIDENT_SIGNUP_PATH}?returnTo=${encodeURIComponent(returnPath)}` : RESIDENT_SIGNUP_PATH;
  }, [location.pathname, location.search]);

  if (!open) return null;

  const message = isLoggedIn ? MESSAGE_LOGGED_IN : MESSAGE_GUEST;
  const primaryLabel = isLoggedIn ? SIGNUP_LABEL : LOGIN_LABEL;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={isLoggedIn ? "not-benefactor-title" : undefined}
    >
      <div
        className="rounded-2xl border border-neutral-200 bg-white shadow-xl max-w-xl w-full p-6 text-center"
        dir="rtl"
      >
        <h2 id="not-benefactor-title" className="text-lg font-semibold text-sa-800 mb-3">
          {isLoggedIn ? "حساب متبرع مطلوب" : "الدخول إلى الخدمة"}
        </h2>
        <p className="text-neutral-700 mb-5">{message}</p>
        <div className="flex flex-row gap-3 justify-center flex-wrap">
          <Link
            to={primaryPath}
            className="inline-flex items-center justify-center min-w-[140px] rounded-xl bg-sa-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
          >
            {primaryLabel}
          </Link>
          {!isLoggedIn && (
            <Link
              to={createAccountPath}
              className="inline-flex items-center justify-center min-w-[140px] rounded-xl border border-sa-300 bg-white px-5 py-2.5 text-sm font-semibold text-sa-700 hover:bg-sa-50 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2"
            >
              {CREATE_ACCOUNT_LABEL}
            </Link>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center min-w-[140px] rounded-xl border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}
